import { useState } from 'react';
import React from 'react';
import { UploadDropzone } from './UploadDropzone';
import { MediaType } from '../types';
import type { IWorkflowStatus } from '@zappar/zw-api-client';
import { CrossIcon } from '../../../icons';
import { getFileTypeIcon, getFileExtension, getMediaTypeFromExtension } from '../utils';
import { useMode } from '../context/ModeContext';

export interface UploadPopupProps {
  isOpen: boolean;
  onClose: () => void;
  onUpload: (files: File[], status: string) => void;
  selectedFiles: File[];
  acceptedFileTypes?: MediaType[];
  onFilesSelect?: (files: File[]) => void;
  isLoading?: boolean;
  workflowStatuses?: IWorkflowStatus[];
}

export const UploadPopup = ({
  isOpen,
  onClose,
  onUpload,
  onFilesSelect,
  isLoading = false,
  selectedFiles = [],
  acceptedFileTypes = [],
  workflowStatuses = [],
}: UploadPopupProps) => {
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  const { permissions } = useMode();

  if (!isOpen) return null;

  const handleFileInput = (files: FileList) => {
    const fileArray = Array.from(files);
    const allFiles = [...selectedFiles, ...fileArray];
    if (fileArray.length > 0) {
      onFilesSelect?.(allFiles);
    }
  };

  const handleUpload = () => {
    if (selectedFiles.length > 0) {
      onUpload(selectedFiles, selectedStatus);
      onFilesSelect?.([]);
      setSelectedStatus('ready');
    }
  };

  const handleRemoveFile = (index: number) => {
    const updatedFiles = selectedFiles.filter((_, i) => i !== index);
    onFilesSelect?.(updatedFiles);
  };

  const handleCancel = () => {
    onFilesSelect?.([]);
    setSelectedStatus('ready');
    onClose();
  };

  return (
    <div className="upload-popup-overlay" onClick={handleCancel}>
      <div className="upload-popup" onClick={(e) => e.stopPropagation()}>
        <div className="upload-popup-header">
          <h2>Upload {selectedFiles.length} file{selectedFiles.length > 1 ? 's' : ''}...</h2>
        </div>

        <div className="upload-popup-body">
          <div>
            <UploadDropzone onUpload={handleFileInput} acceptedFileTypes={acceptedFileTypes} />
            <div className="upload-popup-info">
              <span>Supported formats: GLB, GLTF, JPEG, PNG, MP4, MP3, and WebM. </span>
              <p>Max 20MB</p>
            </div>
          </div>

          <div className="upload-popup-status-section">
            <label htmlFor="file-status">
              Apply status to all files
            </label>
            <div>
              <select
                id="file-status"
                className=""
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                disabled={isLoading || permissions?.canChangeStatus === false}
              >
                <option value=""> None </option>
                {workflowStatuses.map((s) => (
                  <option key={s.uid} value={s.uid}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="upload-popup-file-list">
            <div className="upload-popup-files">
              {selectedFiles.map((file, index) => (
                <div key={index} className="upload-popup-file-item">
                  <div className="upload-popup-file-info">
                    {React.createElement(getFileTypeIcon(getMediaTypeFromExtension(getFileExtension(file.name))))}
                    <span className="upload-popup-file-name">{file.name}</span>
                  </div>
                  <button
                    className="btn btn-ghost p0"
                    onClick={() => handleRemoveFile(index)}
                    disabled={isLoading}
                    aria-label={`Remove ${file.name}`}
                  >
                    <CrossIcon width={16} height={16} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="upload-popup-actions">
            <button
              className="btn btn-small btn-secondary"
              onClick={handleCancel}
            >
              Cancel
            </button>
            <button
              className="btn btn-small btn-primary"
              onClick={handleUpload}
            >
              Upload asset{selectedFiles.length > 1 ? 's' : ''}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};